import React, { useState } from 'react';
import { Facebook, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { weeklyMenuItems } from '../mock';

const Menu = () => {
  const [activeTab, setActiveTab] = useState('weekly');

  const groupByCategory = (items) => {
    return items.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});
  };

  const groupedMenu = groupByCategory(weeklyMenuItems);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-nanny-cream">
      {/* Hero Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nanny-dark mb-6 heading-font">Our Menu</h1>
            <p className="text-xl text-gray-700 mb-6">
              Fresh, homemade meals prepared daily with love and care
            </p>
            <div className="bg-white rounded-lg p-6 shadow-lg inline-block">
              <p className="text-nanny-maroon font-semibold mb-2">In-Store Hours</p>
              <p className="text-gray-700">Monday - Thursday</p>
              <p className="text-2xl font-bold text-nanny-dark">10:30 AM - 5:30 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-12">
              <TabsTrigger value="weekly" className="text-lg">Weekly Take-Home Menu</TabsTrigger>
              <TabsTrigger value="catering" className="text-lg">Catering Menu</TabsTrigger>
            </TabsList>

            {/* Weekly Menu */}
            <TabsContent value="weekly" className="space-y-8">
              <div className="bg-nanny-maroon text-white rounded-xl p-8 mb-8">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <Facebook className="h-6 w-6" />
                  <h2 className="text-2xl font-bold">Check Facebook for This Week's Menu!</h2>
                </div>
                <p className="text-center text-nanny-cream mb-6">
                  Our weekly menu changes to keep things fresh and exciting. Follow us on Facebook to see what's cooking this week!
                </p>
                <div className="text-center">
                  <a 
                    href="https://www.facebook.com/profile.php?id=100068874377725" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-white text-nanny-maroon px-6 py-3 rounded-lg font-semibold hover:bg-nanny-cream transition-colors"
                  >
                    <span>View Current Menu on Facebook</span>
                    <ExternalLink className="h-5 w-5" />
                  </a>
                </div>
              </div>

              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-nanny-dark mb-4 heading-font">Sample Menu Items</h3>
                <p className="text-gray-600">Here's a taste of what we typically offer</p>
              </div>

              {Object.entries(groupedMenu).map(([category, items]) => (
                <div key={category} className="mb-12">
                  <h3 className="text-3xl font-bold text-nanny-dark mb-6 pb-3 border-b-2 border-nanny-maroon heading-font">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item) => (
                      <Card key={item.id} className="hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-nanny-maroon">
                        <CardContent className="p-6">
                          <h4 className="text-xl font-bold text-nanny-dark mb-2">{item.name}</h4>
                          <p className="text-gray-600 text-sm">{item.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-nanny-cream rounded-xl p-8 text-center">
                <p className="text-lg text-gray-700 mb-4">
                  If you don't see something on the menu that you would like to have or have any questions, please call
                </p>
                <a href="tel:731-267-9884" className="text-3xl font-bold text-nanny-maroon hover:text-nanny-dark transition-colors">
                  731-267-9884
                </a>
              </div>
            </TabsContent>

            {/* Catering Menu */}
            <TabsContent value="catering" className="space-y-8">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-nanny-dark mb-4 heading-font">Full Catering Services</h3>
                <p className="text-gray-600 text-lg mb-6">Available 7 days a week for Breakfast, Lunch, and Supper</p>
                <div className="flex justify-center space-x-4">
                  <Badge variant="outline" className="text-base py-2 px-4 border-nanny-maroon text-nanny-maroon">Breakfast</Badge>
                  <Badge variant="outline" className="text-base py-2 px-4 border-nanny-maroon text-nanny-maroon">Lunch</Badge>
                  <Badge variant="outline" className="text-base py-2 px-4 border-nanny-maroon text-nanny-maroon">Supper</Badge>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/aigaazza_3a4c49c7-9a26-469d-ad70-08d7bd00813e.png" 
                  alt="Catering Menu" 
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                <Card className="border-2 border-nanny-maroon">
                  <CardHeader>
                    <CardTitle className="text-2xl text-nanny-dark">Popular Catering Options</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-nanny-maroon mr-2">•</span>
                        <span className="text-gray-700">Party Trays (Sandwiches, Wraps, Sliders)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-nanny-maroon mr-2">•</span>
                        <span className="text-gray-700">Dips & Appetizers</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-nanny-maroon mr-2">•</span>
                        <span className="text-gray-700">Breakfast Platters</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-nanny-maroon mr-2">•</span>
                        <span className="text-gray-700">Hot Entrees with Sides</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-nanny-maroon mr-2">•</span>
                        <span className="text-gray-700">Dessert Trays</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="bg-nanny-maroon text-white">
                  <CardHeader>
                    <CardTitle className="text-2xl">Ready to Order?</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-4 text-nanny-cream">
                      Call us to discuss your catering needs. We'll work with you to create the perfect menu for your event!
                    </p>
                    <a href="tel:731-267-9884" className="block text-center bg-white text-nanny-maroon px-6 py-3 rounded-lg font-bold text-xl hover:bg-nanny-cream transition-colors">
                      Call 731-267-9884
                    </a>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Menu;
